// Caller journey — "has this ANI been in before?" list.
//
// Reuses the raw allAiSessions already fetched by
// resolveCallByInteractionId — zero extra API calls. Groups sessions by
// interactionId (same call can span multiple bot sessions) and shows the
// 5 most recent OTHER calls from the same ANI in the search window.
//
// If there's no other activity for the ANI, we render nothing (no card)
// so Business view stays uncluttered on first-time callers.

import React, { useMemo } from 'react';
import { Anchor, Badge, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconHistory } from '@tabler/icons-react';

import { scoreCall } from './concernScore';
import { OutcomeBadge, fmtDuration, fmtTs, short } from './utils';

const MAX_PRIOR_CALLS = 5;

export function CallerJourney({ callerAni, allAiSessions, interactionId, window, setView }) {
  const priorCalls = useMemo(() => {
    if (!callerAni || !Array.isArray(allAiSessions) || allAiSessions.length === 0) return [];
    const grouped = new Map();
    for (const s of allAiSessions) {
      if (!s?.ani || s.ani !== callerAni) continue;
      if (!s?.interactionId || s.interactionId === interactionId) continue;
      const row = normalise(s);
      const existing = grouped.get(row.interactionId);
      if (!existing || (row.startTs ?? 0) > (existing.startTs ?? 0)) {
        grouped.set(row.interactionId, row);
      }
    }
    const list = [...grouped.values()];
    list.sort((a, b) => (b.startTs ?? 0) - (a.startTs ?? 0));
    return list.slice(0, MAX_PRIOR_CALLS);
  }, [callerAni, allAiSessions, interactionId]);

  if (priorCalls.length === 0) return null;

  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="xs" mb="xs">
        <IconHistory size={16} color="#1976d2" />
        <Title order={5}>Caller journey</Title>
        <Badge size="xs" variant="light" color="gray">
          {priorCalls.length} other call{priorCalls.length === 1 ? '' : 's'}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed" mb="sm">
        Other calls from {callerAni} in this search window.
      </Text>
      <Stack gap={6}>
        {priorCalls.map((c) => {
          const concern = scoreCall(c);
          return (
            <Anchor
              key={c.interactionId}
              size="sm"
              style={{ cursor: 'pointer' }}
              onClick={() =>
                setView({
                  name: 'call-detail',
                  interactionId: c.interactionId,
                  window,
                  cachedAiSessions: allAiSessions,
                })
              }
            >
              <Group gap="xs" wrap="nowrap">
                <Text size="xs" c="dimmed" style={{ minWidth: 140 }}>
                  {fmtTs(c.startTs)}
                </Text>
                <OutcomeBadge outcome={c.outcome} />
                <Text size="xs" c="dimmed">
                  {fmtDuration(c.durationMs)} · {c.messageCount ?? 0} msgs
                </Text>
                {concern.score >= 15 && (
                  <Badge size="xs" color={concern.score >= 60 ? 'red' : concern.score >= 35 ? 'orange' : 'yellow'}>
                    concern {concern.score}
                  </Badge>
                )}
                <Text size="xs" c="dimmed" style={{ marginLeft: 'auto', fontFamily: 'monospace' }}>
                  {short(c.interactionId)}
                </Text>
              </Group>
            </Anchor>
          );
        })}
      </Stack>
    </Paper>
  );
}

// Pre-normalisation step so scoreCall + the rendering code can treat
// raw AI-session rows the same way CallSearch does. Keep in sync with
// wxccSearchClient.aiSessionToCallRow().
function normalise(s) {
  return {
    interactionId: s.interactionId,
    sessionId: s.sessionId,
    agentId: s.agentId,
    agentName: s.agentName,
    agentType: s.agentType,
    channelType: s.channelType,
    ani: s.ani,
    dnis: s.dnis,
    entryPoint: s.entryPoint?.name ?? null,
    flow: s.flow?.name ?? null,
    outcome: s.interactionOutcome,
    intent: s.intent,
    lastAction: s.lastAction,
    startTs: s.sessionStartTime,
    endTs: s.sessionEndTime,
    durationMs: s.duration,
    messageCount: s.noOfTotalMessages,
    avgResponseMs: s.averageResponseTime,
    dialogCount: s.dialogCount,
    // Phase 5 fields (optional — present if present in the raw row).
    deadAirCount: s.deadAirCount,
    evalInteractionsFailureCount: s.evalInteractionsFailureCount,
    bargeInCount: s.bargeInCount,
    fulfillmentCount: s.fulfillmentCount,
    welcomeMessageResponseTime: s.welcomeMessageResponseTime,
    overallEvalScore: s.overallEvalScore,
    wordRatioTotalScore: s.wordRatioTotalScore,
  };
}
